import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { Eye, EyeOff, TrendingUp, ExternalLink, BadgeCheck } from "lucide-react";

import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { useAuth } from "@/hooks/useAuth";
import { cn } from "@/lib/utils";

// FIN-4 — HeroSaldoAportadoCard adaptativo OF ON / OF OFF.
//
// OF ON (futuro): badge verde "✓ Sincronizado via Open Finance" no header
// OF OFF (default): banner sutil "Conecte sua corretora pra sincronizar"

const fmtBRL = (v) => Number(v ?? 0).toLocaleString("pt-BR", {
  style: "currency", currency: "BRL",
});

function HiddenAmount() {
  return <span className="font-mono">R$ ••••••</span>;
}

function plural(n, s, p) {
  return `${n} ${n === 1 ? s : p}`;
}

export default function HeroSaldoAportadoCard({
  summary,
  isOpenFinance,
  loading,
}) {
  const { user } = useAuth();
  const userId = user?.id;

  // Toggle eye persistido (mesma key família finance pra UX consistente)
  const STORAGE_KEY = userId ? `nexo_invest_balance_hidden_${userId}` : null;
  const [hidden, setHidden] = useState(() => {
    if (!STORAGE_KEY) return false;
    try { return localStorage.getItem(STORAGE_KEY) === "1"; } catch { return false; }
  });
  useEffect(() => {
    if (!STORAGE_KEY) return;
    try { localStorage.setItem(STORAGE_KEY, hidden ? "1" : "0"); } catch (_) { /* noop */ }
  }, [STORAGE_KEY, hidden]);

  const totalCurrent = Number(summary?.total_current ?? 0);
  const totalPurchase = Number(summary?.total_purchase ?? 0);
  const investmentsCount = Number(summary?.investments_count ?? 0);
  const institutionsCount = Number(summary?.institutions_count ?? 0);

  return (
    <Card
      variant="elevated"
      className="glass-card-strong gradient-border-cyan glow-hover p-6 md:p-8 relative overflow-hidden"
      data-testid="invest-hero"
    >
      {/* Top row */}
      <div className="flex items-start justify-between mb-5 gap-4">
        <div className="flex items-center gap-3 min-w-0">
          <div className="w-10 h-10 rounded-xl bg-brand-green/15 border border-brand-green/30 flex items-center justify-center shrink-0">
            <TrendingUp className="w-5 h-5 text-brand-green" aria-hidden="true" />
          </div>
          <div className="min-w-0">
            <h2 className="font-display font-bold text-foreground text-h2 leading-tight" data-testid="invest-hero-title">
              {isOpenFinance ? "Investimentos" : "Saldo aportado"}
            </h2>
            <p className="text-caption text-foreground-muted">
              {isOpenFinance ? "Sincronizado via Open Finance" : "Atualização manual"}
            </p>
          </div>
        </div>

        <button
          type="button"
          onClick={() => setHidden(h => !h)}
          className="p-2 rounded-lg text-foreground-muted hover:bg-surface-card-hover hover:text-foreground transition-colors"
          aria-label={hidden ? "Mostrar saldo" : "Esconder saldo"}
          data-testid="invest-hero-toggle"
        >
          {hidden ? <Eye className="w-4 h-4" /> : <EyeOff className="w-4 h-4" />}
        </button>
      </div>

      {/* Heroic balance */}
      <motion.div
        initial={{ opacity: 0, y: 6 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.35, ease: [0.2, 0.8, 0.2, 1] }}
        className="mb-3"
        data-testid="invest-hero-amount"
      >
        <div
          className={cn(
            "font-display font-bold leading-[0.95] tracking-[-0.02em] tabular-nums",
            "text-[44px] md:text-[64px] lg:text-[80px]",
            "text-brand-green",
          )}
        >
          {loading ? "…" : (hidden ? <HiddenAmount /> : fmtBRL(totalCurrent))}
        </div>
      </motion.div>

      {/* Total investido caption */}
      <p className="text-caption text-foreground-muted mb-3" data-testid="invest-hero-aportado">
        Total investido: {hidden ? <HiddenAmount /> : fmtBRL(totalPurchase)}
      </p>

      {/* Footer */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
        <p className="text-caption text-foreground-muted" data-testid="invest-hero-footer">
          {plural(investmentsCount, "ativo", "ativos")}
          {" · "}
          {plural(institutionsCount, "instituição", "instituições")}
        </p>

        {isOpenFinance ? (
          <Badge variant="success" className="self-start md:self-auto inline-flex items-center gap-1.5 px-2.5 py-1 text-caption font-normal">
            <BadgeCheck className="w-3.5 h-3.5" aria-hidden="true" />
            Sincronizado via Open Finance
          </Badge>
        ) : (
          <Badge
            variant="info"
            className="self-start md:self-auto inline-flex items-center gap-1.5 px-2.5 py-1 text-caption font-normal"
            data-testid="invest-hero-of-banner"
          >
            <ExternalLink className="w-3.5 h-3.5" aria-hidden="true" />
            Conecte sua corretora pra sincronizar
          </Badge>
        )}
      </div>
    </Card>
  );
}
